import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../../api/client";
import { GlobalContext } from "../../../../context";
import { fmtDay } from "../../home/time";
import Icon from "../Icon";
import { Avatar, Pill, ScopePicker, STATUS, useScopes } from "./staffUi";

const BACKEND = process.env.REACT_APP_DOMAIN_URL || "";
const CAP = { passed: "passed", submitted: "submitted, being marked", redo_required: "redo required (D or F)", not_submitted: "not submitted", left_out: "left out of the team submission" };

function Reqs({ s }) {
  // Readiness is judged on the final activities score: every item counts, missing work as 0.
  const a = s.attendance.percent, act = s.grading?.activities.final ?? s.activity;
  return (
    <div className="sp-reqs">
      <span className={s.meets.attendance ? "ok" : "lo"}>Attendance {a == null ? "—" : `${a}%`}{s.meets.attendance ? " ✓" : ""}</span>
      <span className={s.meets.activities ? "ok" : "lo"}>Activities (final) {act == null ? "—" : act}{s.meets.activities ? " ✓" : ""}</span>
      <span className={s.meets.capstone ? "ok" : "lo"}>Capstone {CAP[s.capstone]}{s.meets.capstone ? " ✓" : ""}</span>
    </div>
  );
}

function Confirm({ title, children, ok, busy, onCancel, onSubmit }) {
  return (
    <div className="sp-modal-wrap">
      <form className="sp-modal" role="dialog" aria-modal="true" aria-labelledby="cf-t" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <h3 id="cf-t">{title}</h3>
        {children}
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
          <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="cbtn go" type="submit" disabled={busy}>{busy ? "Working…" : ok}</button>
        </div>
      </form>
    </div>
  );
}

/** Admin: generate certificates for students who met every requirement, or approve one manually with a note. */
export default function StaffCertificatesPage() {
  const { authState: { data: auth } } = useContext(GlobalContext);
  const isAdmin = !!auth?.user?.is_admin;
  const { scopes, scope, setScope, failed } = useScopes();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("ready");
  const [picked, setPicked] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!scope) return;
    setError(null);
    client.get("/staff/progress", { params: { cohort_id: scope.cohort_id, course_id: scope.course_id } })
      .then((r) => { setData(r.data); setPicked(new Set()); })
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load the students."));
  }, [scope]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (!isAdmin) {
    return <div className="lms"><div className="main single"><div className="view"><section className="complete-box"><div><strong>Only admins issue certificates</strong><small>You can follow each student's progress in My students.</small></div></section></div></div></div>;
  }

  const S = data?.students || [];
  const ready = S.filter((s) => s.ready && !s.certificate?.approved);
  const notYet = S.filter((s) => !s.ready && !s.certificate?.approved);
  const issued = S.filter((s) => s.certificate?.approved);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const scopeBody = scope ? { cohort_id: scope.cohort_id, course_id: scope.course_id } : {};

  const generate = async () => {
    setBusy(true);
    try {
      const r = await client.post("/staff/certificates/generate", { ...scopeBody, user_ids: [...picked] });
      toast.success(r.data.message);
      setModal(null); setTab("issued"); load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "We couldn't generate the certificates. Please try again.");
    }
    setBusy(false);
  };
  const approve = async () => {
    if (note.trim().length < 5) return toast.error("Write why you're approving it. The note is saved with the certificate.");
    setBusy(true);
    try {
      const r = await client.post("/staff/certificates/approve", { ...scopeBody, user_id: modal.s.id, note: note.trim() });
      toast.success(r.data.message);
      setModal(null); setNote(""); setTab("issued"); load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "We couldn't approve this certificate. Please try again.");
    }
    setBusy(false);
    return undefined;
  };

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div><span className="sp-kicker">Learning</span><h1>Certificates</h1><p>{data ? (data.cohort.name.startsWith(data.course.title) ? data.cohort.name : `${data.course.title} · ${data.cohort.name}`) : "Who's ready, who isn't yet, and what's been issued."}</p></div>
        <div className="sp-filters"><ScopePicker scopes={scopes} scope={scope} setScope={setScope} /></div>
      </div>
      <section className="sp-box sp-req-card">
        <b style={{ color: "var(--ink)", fontSize: 14 }}>Requirements</b>
        <div className="sp-reqs"><span>70%+ attendance (live, or 60% of the recording)</span><span>Activities 70+ (quizzes 40%, assignments 60%; missing work counts as 0)</span><span>Capstone graded A, B or C</span></div>
      </section>

      {failed ? <section className="complete-box"><div><strong>We couldn't load your courses</strong><small>Refresh the page to try again.</small></div></section>
        : error ? <section className="complete-box"><div><strong>{error}</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={load}>Try again</button></section>
        : !data ? <div className="skeleton" style={{ height: 300 }} />
        : (
          <>
            <nav className="tabs" role="tablist">
              {[["ready", "Ready", ready.length], ["notyet", "Not yet", notYet.length], ["issued", "Issued", issued.length]].map(([k, l, c]) => (
                <button key={k} className="tab" type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}{c ? <span className="sp-pill brand" style={{ marginLeft: 6, padding: "1px 7px" }}>{c}</span> : null}</button>
              ))}
            </nav>

            {tab === "ready" && (ready.length ? (
              <section className="sp-box">
                <div className="sp-tools">
                  <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }} htmlFor="pick-all">
                    <input id="pick-all" type="checkbox" checked={ready.every((s) => picked.has(s.id))} onChange={(e) => setPicked(e.target.checked ? new Set(ready.map((s) => s.id)) : new Set())} /> Select all {ready.length}
                  </label>
                  <span className="course-tag">{picked.size} selected</span>
                  <span style={{ marginLeft: "auto" }} />
                  <button className="cbtn go" type="button" disabled={!picked.size} onClick={() => setModal({ kind: "generate" })}><Icon name="award" size={16} /> Generate {picked.size || ""} certificate{picked.size === 1 ? "" : "s"}</button>
                </div>
                <div className="sp-table-wrap"><table className="sp-table">
                  <thead><tr><th /><th>Student</th><th>Requirements</th><th>Name on certificate</th></tr></thead>
                  <tbody>{ready.map((s) => (
                    <tr key={s.id}>
                      <td><input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Select ${s.name}`} /></td>
                      <td><div className="sp-stu"><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.email}</small></div></div></td>
                      <td><Reqs s={s} /></td>
                      <td>{s.name}<div className="course-tag" style={{ fontSize: 12 }}>From their profile</div></td>
                    </tr>
                  ))}</tbody>
                </table></div>
                <div className="sp-foot"><span>Check each name is spelled right; it's printed exactly as in the student's profile. They get an email with their certificate.</span></div>
              </section>
            ) : <section className="complete-box"><div><strong>Nobody is waiting</strong><small>Students appear here once they meet every requirement.</small></div></section>)}

            {tab === "notyet" && (
              <section className="sp-box">
                <div className="sp-table-wrap"><table className="sp-table">
                  <thead><tr><th>Student</th><th>What's missing</th><th>Status</th><th /></tr></thead>
                  <tbody>{notYet.map((s) => (
                    <tr key={s.id}>
                      <td><div className="sp-stu"><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.email}</small></div></div></td>
                      <td><Reqs s={s} /></td>
                      <td><Pill cls={STATUS[s.status][0]}>{STATUS[s.status][1]}</Pill></td>
                      <td className="num"><button className="cbtn quiet" type="button" onClick={() => { setNote(""); setModal({ kind: "approve", s }); }}>Approve manually</button></td>
                    </tr>
                  ))}{!notYet.length && <tr><td colSpan={4} className="course-tag">Everyone has met the requirements or has a certificate.</td></tr>}</tbody>
                </table></div>
                <div className="sp-foot"><span>Approve manually when you're satisfied a student has earned it, for example they missed classes for a documented reason. Your note is saved with the certificate.</span></div>
              </section>
            )}

            {tab === "issued" && (
              <section className="sp-box">
                <div className="sp-table-wrap"><table className="sp-table">
                  <thead><tr><th>Student</th><th>Certificate no.</th><th>Issued</th><th>How</th><th /></tr></thead>
                  <tbody>{issued.map((s) => (
                    <tr key={s.id}>
                      <td><div className="sp-stu"><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.email}</small></div></div></td>
                      <td>{s.certificate.number}</td>
                      <td>{s.certificate.issue_date ? fmtDay(s.certificate.issue_date) : "—"}</td>
                      <td>{s.certificate.manual ? <Pill cls="brand">Approved manually</Pill> : <Pill cls="ok">Met requirements</Pill>}{s.certificate.note && <div className="course-tag" style={{ fontSize: 12, maxWidth: 280 }}>"{s.certificate.note}"</div>}</td>
                      <td className="num"><a className="link-btn" href={`${BACKEND}/certificate/${s.certificate.uuid}/view`} target="_blank" rel="noopener noreferrer">View PDF</a></td>
                    </tr>
                  ))}{!issued.length && <tr><td colSpan={5} className="course-tag">No certificates issued for this course yet.</td></tr>}</tbody>
                </table></div>
              </section>
            )}
          </>
        )}

      {modal?.kind === "generate" && (
        <Confirm title={`Generate ${picked.size} certificate${picked.size === 1 ? "" : "s"}?`} ok="Generate and email" busy={busy} onCancel={() => setModal(null)} onSubmit={generate}>
          <p>{S.filter((s) => picked.has(s.id)).map((s) => s.name).join(", ")}</p>
          <div className="sp-warn">Each student gets their certificate by email, with the name on their profile.</div>
        </Confirm>
      )}
      {modal?.kind === "approve" && (
        <Confirm title={`Approve ${modal.s.name}'s certificate?`} ok="Approve and generate" busy={busy} onCancel={() => setModal(null)} onSubmit={approve}>
          <p>They haven't met every requirement:</p>
          <Reqs s={modal.s} />
          <label className="sp-field" htmlFor="ap-note">Why you're approving it
            <textarea id="ap-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} autoFocus placeholder="For example: missed weeks 4–5 for a hospital stay; caught up on every recording and passed the capstone." />
          </label>
          <div className="sp-warn">The certificate is generated and emailed straight away, and your note is saved with it.</div>
        </Confirm>
      )}
    </div></div></div>
  );
}
